import { useEffect, useId, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { Blackout, BlackoutCreate, BlackoutLevel, Team } from '@lms/shared-types'
import { ApiError } from '../../api/client'
import {
  createBlackout,
  previewAffectedLeaves,
  updateBlackout,
  type AffectedLeavePreviewItem,
} from '../../api/blackouts'
import { DateField } from '../primitives/DateField'
import { Field } from '../primitives/Field'
import { fmtDateRange } from '../../lib/format'

export type BlackoutFormProps =
  | {
      mode: 'create'
      teams: Team[]
      onClose: () => void
      onSaved: () => void
      existing?: never
    }
  | {
      mode: 'edit'
      existing: Blackout
      teams: Team[]
      onClose: () => void
      onSaved: () => void
    }

export function BlackoutForm(props: BlackoutFormProps): JSX.Element {
  const { mode, teams, onClose, onSaved } = props
  const [form, setForm] = useState<BlackoutCreate>(() =>
    mode === 'edit'
      ? {
          name: props.existing.name,
          startDate: props.existing.startDate,
          endDate: props.existing.endDate,
          teamIds: [...props.existing.teamIds],
          level: props.existing.level,
          note: props.existing.note ?? null,
        }
      : {
          name: '',
          startDate: new Date().toISOString().slice(0, 10),
          endDate: new Date().toISOString().slice(0, 10),
          teamIds: [],
          level: 'director',
          note: null,
        },
  )
  const [error, setError] = useState<string | null>(null)
  const uid = useId()

  const mutation = useMutation({
    mutationFn: async (input: BlackoutCreate): Promise<Blackout> =>
      mode === 'edit'
        ? updateBlackout(props.existing.id, input)
        : createBlackout(input),
    onSuccess: () => onSaved(),
    onError: (err) => {
      const status = err instanceof ApiError ? err.status : 500
      const msg = err instanceof Error ? err.message : 'Save failed'
      setError(`${msg} (${status})`)
    },
  })

  const toggleTeam = (id: string): void => {
    setForm((f) => ({
      ...f,
      teamIds: f.teamIds.includes(id)
        ? f.teamIds.filter((x) => x !== id)
        : [...f.teamIds, id],
    }))
  }

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setError(null)
    if (form.teamIds.length === 0) {
      setError('Pick at least one team.')
      return
    }
    if (form.endDate < form.startDate) {
      setError('End date must be on or after start date.')
      return
    }
    mutation.mutate(form)
  }

  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Field label="Name" htmlFor={`${uid}-name`} required>
        <input
          id={`${uid}-name`}
          type="text"
          required
          maxLength={100}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="blackout-name"
          placeholder="e.g. Quarter close, Year-end freeze"
          style={{
            padding: '8px 10px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: 'var(--surface)',
            fontSize: 13,
            fontFamily: 'inherit',
          }}
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Start date" htmlFor={`${uid}-start`} required>
          <DateField
            value={form.startDate || null}
            onChange={(iso) => setForm({ ...form, startDate: iso ?? '' })}
          />
        </Field>
        <Field label="End date" htmlFor={`${uid}-end`} required>
          <DateField
            value={form.endDate || null}
            min={form.startDate || undefined}
            onChange={(iso) => setForm({ ...form, endDate: iso ?? '' })}
          />
        </Field>
      </div>

      <Field label="Escalation level" htmlFor={`${uid}-level`}>
        <select
          id={`${uid}-level`}
          value={form.level}
          onChange={(e) => setForm({ ...form, level: e.target.value as BlackoutLevel })}
          style={{
            padding: '8px 10px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: 'var(--surface)',
            fontSize: 13,
          }}
        >
          <option value="head">Head — bookings escalated to HOD</option>
          <option value="director">Director — bookings escalated to director</option>
          <option value="exec_director">
            Exec Director — bookings escalated to exec director
          </option>
        </select>
      </Field>

      <Field label="Note (employee-visible)" htmlFor={`${uid}-note`}>
        <textarea
          id={`${uid}-note`}
          rows={3}
          maxLength={1000}
          value={form.note ?? ''}
          onChange={(e) =>
            setForm({ ...form, note: e.target.value || null })
          }
          data-testid="blackout-note"
          placeholder="Shown to employees who try to book leave in this window. e.g. All hands on deck — major launch window."
          style={{
            padding: '8px 10px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: 'var(--surface)',
            fontSize: 13,
            fontFamily: 'inherit',
            resize: 'vertical',
            width: '100%',
            boxSizing: 'border-box',
          }}
        />
      </Field>

      <fieldset
        style={{
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 12,
          margin: 0,
        }}
      >
        <legend
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text-2)',
            padding: '0 4px',
          }}
        >
          Affected teams
        </legend>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 8,
          }}
        >
          {teams.map((t) => (
            <label
              key={t.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: 'var(--text-2)',
              }}
            >
              <input
                type="checkbox"
                checked={form.teamIds.includes(t.id)}
                onChange={() => toggleTeam(t.id)}
              />
              {t.name}
            </label>
          ))}
        </div>
      </fieldset>

      <AffectedLeavesPreview
        startDate={form.startDate}
        endDate={form.endDate}
        teamIds={form.teamIds}
      />

      {error ? (
        <div
          role="alert"
          style={{
            padding: '10px 12px',
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            border: '1px solid var(--danger-line)',
            borderRadius: 10,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          style={{
            padding: '8px 14px',
            background: 'var(--surface)',
            color: 'var(--text-2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontWeight: 600,
            cursor: mutation.isPending ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          data-testid="save-blackout"
          disabled={mutation.isPending}
          style={{
            padding: '8px 14px',
            background: mutation.isPending ? 'var(--border-strong)' : 'var(--steel-blue)',
            color: 'var(--surface)',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            cursor: mutation.isPending ? 'not-allowed' : 'pointer',
          }}
        >
          {mutation.isPending
            ? mode === 'edit'
              ? 'Saving…'
              : 'Creating…'
            : mode === 'edit'
              ? 'Save changes'
              : 'Create blackout'}
        </button>
      </div>
    </form>
  )
}

interface AffectedLeavesPreviewProps {
  startDate: string
  endDate: string
  teamIds: string[]
}

/**
 * Live preview inside the create / edit blackout modal. Shows a one-line
 * summary that's always visible plus an expandable card stack of every
 * approved leave whose dates overlap the proposed window. Debounced 300ms
 * to avoid hitting the backend on every keystroke. Empty / invalid dates
 * suppress the request entirely.
 */
function AffectedLeavesPreview({
  startDate,
  endDate,
  teamIds,
}: AffectedLeavesPreviewProps): JSX.Element | null {
  const [expanded, setExpanded] = useState(false)
  const [debounced, setDebounced] = useState({ startDate, endDate, teamIds })

  useEffect(() => {
    const handle = setTimeout(
      () => setDebounced({ startDate, endDate, teamIds }),
      300,
    )
    return () => clearTimeout(handle)
  }, [startDate, endDate, teamIds])

  const datesValid =
    Boolean(debounced.startDate) &&
    Boolean(debounced.endDate) &&
    debounced.endDate >= debounced.startDate

  const query = useQuery({
    queryKey: ['blackout-preview-affected', debounced],
    queryFn: () =>
      previewAffectedLeaves({
        startDate: debounced.startDate,
        endDate: debounced.endDate,
        teamIds: debounced.teamIds,
      }),
    enabled: datesValid,
  })

  if (!datesValid) return null

  const items: AffectedLeavePreviewItem[] = query.data ?? []
  const count = items.length

  return (
    <div
      data-testid="affected-leaves-preview"
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        background:
          count > 0 ? 'var(--warning-bg, #fef3c7)' : 'var(--surface-2)',
        padding: '10px 12px',
      }}
    >
      <button
        type="button"
        data-testid="toggle-affected-preview"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        disabled={count === 0}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 0,
          background: 'none',
          border: 'none',
          color: count > 0 ? 'var(--warning-fg, #92400e)' : 'var(--text-3)',
          fontSize: 13,
          fontWeight: 600,
          cursor: count === 0 ? 'default' : 'pointer',
          textAlign: 'left',
        }}
      >
        <span data-testid="affected-preview-summary">
          {query.isLoading
            ? 'Checking who will be affected…'
            : query.isError
              ? 'Could not load affected leaves.'
              : count === 0
                ? 'No approved leaves overlap this window.'
                : `${count} approved leave${count === 1 ? '' : 's'} will be re-routed when this blackout is saved.`}
        </span>
        {count > 0 ? (
          <span aria-hidden="true" style={{ fontSize: 11 }}>
            {expanded ? '▲' : '▼'}
          </span>
        ) : null}
      </button>

      {expanded && count > 0 ? (
        <ul
          role="list"
          data-testid="affected-leaves-list"
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '10px 0 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {items.map((item) => (
            <AffectedLeaveCard key={item.leaveId} item={item} />
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function AffectedLeaveCard({ item }: { item: AffectedLeavePreviewItem }): JSX.Element {
  const [open, setOpen] = useState(false)
  return (
    <li
      data-testid="affected-leave-card"
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--french-gray-2)',
        borderRadius: 6,
        padding: '8px 10px',
        fontSize: 12,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          background: 'none',
          border: 'none',
          padding: 0,
          fontSize: 12,
          cursor: 'pointer',
          color: 'var(--text)',
          textAlign: 'left',
        }}
      >
        <span style={{ fontWeight: 600 }}>{item.employeeName}</span>
        <span style={{ color: 'var(--text-2)' }}>
          {fmtDateRange(item.startDate, item.endDate)}
          {' · '}
          {item.daysCount}d {item.type}
        </span>
      </button>
      {open ? (
        <div
          style={{
            marginTop: 6,
            paddingTop: 6,
            borderTop: '1px solid var(--french-gray-2)',
            color: 'var(--text-2)',
            fontSize: 12,
          }}
        >
          <strong style={{ color: 'var(--text)' }}>Reason: </strong>
          {item.reason || '—'}
        </div>
      ) : null}
    </li>
  )
}
